import React, { useState, useEffect, useMemo } from 'react'
import { Modal, Button, Form } from 'semantic-ui-react'
import PropTypes from "prop-types";
import { SetRoomPlannedMaintenance } from '../../api/services/pms';
import { onSuccess, onError, onWarning } from '../shared/messages'
import WeekDaysSelect from '../shared/WeekDaysSelect';
import { useTextResources } from '../../language';
import { DatePicker } from '../shared/DatePicker';
import moment from 'moment';
import { useLoader } from '../shared/utils';
import RoomsDropDown from '../shared/RoomsDropDown';
import HotelsDropDown from '../shared/HotelsDropDown';
import { isMobileOnly } from 'mobile-device-detect';
import ValidateModalPermission from '../shared/ValidateModalPermission';
import { PermissionsActions } from '../shared/permissionsActions';


const RoomMaintenancePopup = ({ onClose, maintenance: initialMaintenance, onSave, ...rest }) => {
    const text = useTextResources()
    const loader = useLoader()
    const [maintenance, setMaintenance] = useState({})
    const [isSaving, setIsSaving] = useState(false)
    const [businessDate, setBusinessDate] = useState(new Date())

    useEffect(() => {
        setBusinessDate(new Date())
    }, [])

    useEffect(() => {
        if (initialMaintenance) {
            initialMaintenance.StartDate = moment(initialMaintenance.StartDate).toDate()
            initialMaintenance.EndDate = moment(initialMaintenance.EndDate).toDate()
        }
        setMaintenance(initialMaintenance || {})
    }, [initialMaintenance])

    const saveAction = async () => {
        setIsSaving(true)
        loader(true)
        try {
            const updated = await SetRoomPlannedMaintenance({ status: maintenance, forceUpdate: false })
            if (onSave) {
                onSave(updated)
            }
            onSuccess()
            if (onClose) onClose()
        } catch (error) {
            if (error.status === 400 && error.error) {
                error.error.ErrorMessage = error.error.ErrorMessage.split(".")[0]
            }
            error.status === 400 ? onWarning(error) : onError(error)
        }
        loader(false)
        setIsSaving(false)
    }
    const deleteAction = async () => {
        setIsSaving(true)
        loader(true)
        try {
            maintenance.Id *= -1
            const updated = await SetRoomPlannedMaintenance({ status: maintenance, forceUpdate: false })
            if (onSave) {
                onSave(updated)
            }
            onSuccess()
        } catch (error) {
            console.error(error)
            error.status === 400 ? onWarning(error) : onError(error)
        }
        loader(false)
        setIsSaving(false)
        if (onClose) onClose()
    }

    const closeAction = () => {
        if (onClose) onClose()
    }

    const setStartDate = val => {
        setMaintenance(prev => { return { ...prev, StartDate: val } })
    }
    const setEndDate = val => {
        setMaintenance(prev => { return { ...prev, EndDate: val } })
    }
    const setDescription = val => {
        setMaintenance(prev => { return { ...prev, Description: val } })
    }
    const setDays = val => {
        setMaintenance(prev => { return { ...prev, MaintenanceDays: val } })
    }
    const setRoom = val => {
        setMaintenance(prev => { return { ...prev, RoomId: val?.Id, RoomTypeId: val?.TypeId } })
    }

    const RoomsSpec = React.useMemo(() => {
        return (maintenance && maintenance.HotelId) ? {
            HotelID: maintenance.HotelId
        } : null
    }, [maintenance.HotelId])

    const txtDelete = text("Delete")

    const memoDates = useMemo(() => {
        return maintenance ? [{ startDate: maintenance.StartDate, endDate: maintenance.EndDate }] : []
    }, [maintenance.StartDate, maintenance.EndDate])

    return (
        <ValidateModalPermission actionId={PermissionsActions.ViewRoomPlannedMaintenancePopup} open={rest.open} onClose={onClose}>
            <Modal {...rest} size="tiny">
                <Modal.Header>{text("OutOfOrder")}</Modal.Header>
                <Modal.Content>
                    <Form>
                        {isMobileOnly && <Form.Field control={HotelsDropDown} spec={{ IsActive: true }} label={text("Hotel")} onChange={id => setMaintenance({
                            ...maintenance,
                            HotelId: id
                        })} value={maintenance.HotelId} />}
                        <Form.Field label={text("Room")} control={RoomsDropDown}
                            disabled={maintenance.Id !== 0}
                            spec={RoomsSpec}
                            clearable
                            value={maintenance.RoomId}
                            onChangeItem={setRoom}
                            onValueNotFound={() => setRoom(null)}
                        />
                        <Form.Group widths={2} unstackable>
                            <DatePicker
                                onChange={setStartDate}
                                label={text("StartDate")}
                                minDate={moment(businessDate).startOf("day").toDate()}
                                value={maintenance.StartDate} />
                            <DatePicker
                                onChange={setEndDate}
                                label={text("EndDate")}
                                minDate={maintenance.StartDate}
                                value={maintenance.EndDate}
                                pointing="right" />
                        </Form.Group>
                        {(maintenance.Id === 0) && <Form.Field label={text("Weekdays")} control={WeekDaysSelect} onChange={setDays} dates={memoDates} />}
                        <Form.TextArea label={text("Description")} onChange={(e, { value }) => setDescription(value)} value={maintenance.Description} />
                    </Form>
                </Modal.Content>
                <Modal.Actions>
                    <Button disabled={isSaving} negative size="small" onClick={closeAction}>{text("Close")}</Button>
                    {(maintenance.Id !== 0) && <Button disabled={isSaving} loading={isSaving} negative size="small" onClick={deleteAction}>{txtDelete}</Button>}
                    <Button disabled={isSaving} loading={isSaving} positive size="small" onClick={saveAction}>{text("Save")}</Button>
                </Modal.Actions>
            </Modal>
        </ValidateModalPermission>
    )
}

RoomMaintenancePopup.propTypes = {
    onClose: PropTypes.func,
    maintenance: PropTypes.object,
    onSave: PropTypes.func
}

export default RoomMaintenancePopup
